import {
    useEffect,
    useState,
    useRef
} from "react";
import {
    AppState,
    Animated,
    Platform
} from "react-native";
import type {
    ChartEntranceConstantType,
    ChartEntrance
} from "./type";
import {
    CHART_STAGGER_LIMIT
} from "./util";
import {
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useReducedMotion,
    getMotionTiming,
    MOTION_EASINGS
} from "../../helpers/motion";

export const CHART_ENTRANCE_START = 0;

export const CHART_ENTRANCE_END = 1;

export const CHART_ENTRANCE_VISIBLE_RATIO = 0.25;

type ChartEntrancePhase = "waiting" | "playing" | "done";

type ChartEntranceObservedNode = {
    getBoundingClientRect: () => unknown;
};

type ChartEntranceDocument = {
    removeEventListener: (type: string, listener: () => void) => void;
    addEventListener: (type: string, listener: () => void) => void;
    visibilityState?: string;
};

type ChartEntranceObserverEntry = {
    intersectionRatio: number;
    isIntersecting: boolean;
};

type ChartEntranceObserver = {
    observe: (node: ChartEntranceObservedNode) => void;
    disconnect: () => void;
};

type ChartEntranceObserverConstructor = new (
    callback: (entries: Array<ChartEntranceObserverEntry>) => void,
    options: { threshold: Array<number> }
) => ChartEntranceObserver;

const isObservedNode = (node: unknown): node is ChartEntranceObservedNode => {
    return typeof node === "object" && node !== null && "getBoundingClientRect" in node && typeof node.getBoundingClientRect === "function";
};

const getEntranceDocument = (): ChartEntranceDocument | undefined => {
    const doc: unknown = Reflect.get(globalThis, "document");

    if(typeof doc !== "object" || doc === null || !("addEventListener" in doc) || !("removeEventListener" in doc)) {
        return undefined;
    }

    return doc as ChartEntranceDocument;
};

const getEntranceObserver = (): ChartEntranceObserverConstructor | undefined => {
    const observer: unknown = Reflect.get(globalThis, "IntersectionObserver");

    return typeof observer === "function" ? observer as ChartEntranceObserverConstructor : undefined;
};

const getIsPageVisible = (): boolean => {
    if(Platform.OS !== "web") {
        return AppState.currentState === "active" || AppState.currentState === "unknown";
    }

    return getEntranceDocument()?.visibilityState !== "hidden";
};

const useChartEntrance = ({
    customTheme,
    isEnabled,
    targetRef,
    isReady
}: ChartEntranceConstantType): ChartEntrance => {
    const {
        durations
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const hasShownFinalRef = useRef<boolean>(false);

    const isFadeRef = useRef<boolean>(isReduceMotionEnabled);

    const [
        phase,
        setPhase
    ] = useState<ChartEntrancePhase>("waiting");

    const [
        isPageVisible,
        setIsPageVisible
    ] = useState<boolean>(getIsPageVisible);

    const [
        isInView,
        setIsInView
    ] = useState<boolean>(false);

    const [
        value,
        setValue
    ] = useState<number>(CHART_ENTRANCE_START);

    if(!isEnabled && isReady && phase === "waiting") {
        hasShownFinalRef.current = true;
    }

    const isWaiting = isEnabled && phase === "waiting" && !hasShownFinalRef.current;

    useEffect(() => {
        if(!isWaiting) {
            return;
        }

        if(Platform.OS !== "web") {
            const subscription = AppState.addEventListener("change", (state) => {
                setIsPageVisible(state === "active");
            });

            return () => {
                subscription.remove();
            };
        }

        const doc = getEntranceDocument();

        if(!doc) {
            return;
        }

        const handleVisibilityChange = () => {
            setIsPageVisible(doc.visibilityState !== "hidden");
        };

        doc.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            doc.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [
        isWaiting
    ]);

    useEffect(() => {
        if(!isWaiting) {
            return;
        }

        const node: unknown = targetRef?.current;

        const Observer = getEntranceObserver();

        if(Platform.OS !== "web" || !Observer || !isObservedNode(node)) {
            setIsInView(true);

            return;
        }

        const observer = new Observer((entries) => {
            const entry = entries[entries.length - 1];

            if(entry) {
                setIsInView(entry.isIntersecting && entry.intersectionRatio >= CHART_ENTRANCE_VISIBLE_RATIO);
            }
        }, {
            threshold: [
                CHART_ENTRANCE_VISIBLE_RATIO
            ]
        });

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [
        isWaiting,
        targetRef
    ]);

    useEffect(() => {
        if(!isWaiting || !isReady || !isInView || !isPageVisible) {
            return;
        }

        isFadeRef.current = isReduceMotionEnabled;

        setValue(CHART_ENTRANCE_START);

        setPhase("playing");
    }, [
        isPageVisible,
        isWaiting,
        isInView,
        isReady
    ]);

    useEffect(() => {
        if(phase !== "playing") {
            return;
        }

        const isFade = isFadeRef.current;

        const animatedValue = new Animated.Value(CHART_ENTRANCE_START);

        const timing = getMotionTiming({
            motion: isFade ? "fade" : undefined,
            isReduceMotionEnabled: isFade,
            intent: "chartEnter",
            durations
        });

        const listenerID = animatedValue.addListener(({
            value: currentValue
        }) => setValue(currentValue));

        const animation = Animated.timing(animatedValue, {
            duration: isFade ? timing.duration : timing.duration + CHART_STAGGER_LIMIT * durations.stagger,
            easing: isFade ? timing.easing : MOTION_EASINGS.linear,
            toValue: CHART_ENTRANCE_END,
            useNativeDriver: false
        });

        animation.start(({
            finished
        }) => {
            if(finished) {
                setPhase("done");
            }
        });

        return () => {
            animation.stop();

            animatedValue.removeListener(listenerID);
        };
    }, [
        phase
    ]);

    if(phase === "playing") {
        return {
            progress: isFadeRef.current ? CHART_ENTRANCE_END : value,
            opacity: isFadeRef.current ? value : CHART_ENTRANCE_END,
            isPlaying: true
        };
    }

    if(isWaiting) {
        return {
            opacity: isReduceMotionEnabled ? CHART_ENTRANCE_START : CHART_ENTRANCE_END,
            progress: CHART_ENTRANCE_START,
            isPlaying: false
        };
    }

    return {
        progress: CHART_ENTRANCE_END,
        opacity: CHART_ENTRANCE_END,
        isPlaying: false
    };
};
export default useChartEntrance;
